/**
 * Employees repository — data-access home for the `employees` slice.
 *
 * Consolidates scattered `supabase.from('employees')` / `employee_property_assignments` /
 * `employee_ratings` calls from hooks into one place. Follows the reference pattern
 * set by `serviceRequests` and `receipts` repositories.
 *
 * ── Consolidation scope ────────────────────────────────────────────────────
 *   MOVED here: useEmployees, useEmployee (by id), useCreateEmployee,
 *     useUpdateEmployee, useDeleteEmployee, useEmployeeRecord (by user_id),
 *     useEmployeeRatings, useAllEmployeeRatings, useAddManagerRating,
 *     useEmployeePropertyAssignments, useAssignProperties,
 *     useUnassignProperties, useClearAssignments.
 *
 *   KEPT on Supabase (not consolidated — see authz split):
 *     - CustomerRating.tsx submit_customer_rating (public unguarded token)
 *
 * ── Flip scope ────────────────────────────────────────────────────────────
 *   NEST-ONLY, no fallback: list, getById, create, update, remove, count
 *     (employees CRUD). `public.employees` lost its company-wide
 *     "Users can view company employees" SELECT policy (Track E — asserted
 *     ABSENT by supabase/db-tests/tests/005_two_layer_access_model.sh), and
 *     `employees_with_status` is a security_invoker view over it, so those
 *     fallbacks could only return a silent `[]` (or 42501 on a RETURNING
 *     write) for every non-super-admin session. Removing them turns a masked
 *     Nest outage into a loud NestRequestError.
 *
 *   FLIPPED to Nest, Supabase fallback still ARMED (the fallback still works):
 *     - listAssignments, assignProperties, unassignProperties, clearAssignments
 *       — `employee_property_assignments` keeps a live PERMISSIVE ALL policy
 *       ("Company staff can manage property assignments").
 *     - listRatings, listAllRatings, createManagerRating — `employee_ratings`
 *       keeps a live "Users can view ratings in their company" SELECT policy.
 *
 *   KEPT on Supabase:
 *     - getRecordByUserId (employee portal — authz split: employee role lacks
 *       the employees.view permission key; the guarded endpoint would 403).
 *       Still served by the live self-view policy
 *       "Employees can view themselves" (user_id = auth.uid()).
 */
import {
  isDefinitiveNotFound,
  reportNestFallback,
  reportNestFallbackOrRethrowUnknownCommit,
} from "@/lib/nestFallback";
import { supabase } from "@/integrations/supabase/client";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest } from "@/services/nestRequest";

// ── Types ──────────────────────────────────────────────────────────────────

export interface Employee {
  id: string;
  company_id: string;
  user_id: string | null;
  full_name: string;
  full_name_en: string | null;
  email: string | null;
  phone: string | null;
  job_role: string;
  department: string | null;
  hire_date: string | null;
  is_active: boolean | null;
  /**
   * DEAD FIELD — no longer drives any behaviour. The Field-Agent Payouts "field
   * agents only" filter now scopes by the employee's assigned `field_agent`
   * custom role (role_permission_id → role_permissions.role_name), not this flag.
   * Still populated so the DTO shape stays stable for the parity suite.
   * TODO: drop column + field after the paid_per_task migration is reverted
   * (follow-up to PR #2930).
   */
  paid_per_task: boolean;
  invitation_status: string;
  notes: string | null;
  role_permission_id: string | null;
  permissions: string[];
  created_at: string;
  updated_at: string;
  /** Display-friendly custom role name from the linked role_permissions row. */
  role_display_name?: string | null;
  /**
   * Derived status (not_invited / not_verified / verified) computed from
   * invitation_status + user_id + nafath verification presence.
   * Present in employees_with_status view; set manually in the Supabase path.
   */
  status?: string | null;
}

export interface CreateEmployeeData {
  full_name: string;
  full_name_en?: string;
  email?: string;
  phone?: string;
  job_role: string;
  department?: string;
  hire_date?: string;
  notes?: string;
  role_permission_id?: string | null;
}

export interface EmployeeRating {
  id: string;
  company_id: string;
  employee_id: string;
  rating_type: "customer" | "manager" | "system";
  rating: number;
  task_id: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  rated_by: string | null;
  criteria: string | null;
  rating_period: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmployeePropertyAssignment {
  id: string;
  company_id: string;
  employee_id: string;
  property_id: string;
  assigned_by: string | null;
  assigned_at: string;
}

export interface CreateRatingRequestData {
  employeeId: string;
  taskId: string;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
}

/** `rating_requests` row — the customer-redeemable rating token. */
export interface RatingRequestRow {
  id: string;
  company_id: string;
  employee_id: string;
  task_id: string;
  token: string;
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  status: "pending" | "completed" | "expired";
  sent_at: string;
  completed_at: string | null;
  expires_at: string;
  created_at: string;
}

interface ScopeOptions {
  companyId: string | null | undefined;
  /** When false, skip the company_id filter (super-admin cross-company access). */
  scopeToCompany?: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const TABLE_EMPLOYEES = "employees";
const TABLE_ASSIGNMENTS = "employee_property_assignments";
const TABLE_RATINGS = "employee_ratings";

// Authenticated Nest request bound to the "employees" slice.
const nestRequest = makeNestRequest("employees");

/**
 * The auto-generated Supabase types (src/integrations/supabase/types.ts, never
 * hand-edited) don't yet carry the `paid_per_task` column added in migration
 * 20260628120000. Until they're regenerated, normalize a raw employees / status-
 * view row into the Employee shape — defaulting the flag to the DB default (true)
 * when the column is absent at runtime (older row / pre-migration env). The Nest
 * path already returns the field directly.
 *
 * TODO: the field-agents-only filter no longer reads paid_per_task (it scopes by
 * the field_agent custom role). Drop this helper + the column after the
 * paid_per_task migration is reverted (follow-up to PR #2930).
 */
function withPaidPerTask<T extends object>(row: T): T & { paid_per_task: boolean } {
  const flag = (row as { paid_per_task?: boolean | null }).paid_per_task;
  return { ...row, paid_per_task: flag ?? true };
}

// ── Supabase implementations (fallback / keep-on-Supabase methods) ─────────

// ── Employee record by user_id (kept on Supabase — employee portal) ────────

async function getRecordByUserIdViaSupabase(
  userId: string,
): Promise<Employee | null> {
  const { data, error } = await supabase
    .from(TABLE_EMPLOYEES)
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return data ? (withPaidPerTask(data) as Employee) : null;
}

// ── Employee property assignments (Supabase implementations) ───────────────

async function listAssignmentsViaSupabase(
  employeeId: string,
  companyId: string,
): Promise<EmployeePropertyAssignment[]> {
  const { data, error } = await supabase
    .from(TABLE_ASSIGNMENTS)
    .select("*")
    .eq("employee_id", employeeId)
    .eq("company_id", companyId);
  if (error) throw error;
  return (data ?? []) as EmployeePropertyAssignment[];
}

async function assignPropertiesViaSupabase(
  employeeId: string,
  companyId: string,
  propertyIds: string[],
  assignedBy: string | null,
): Promise<EmployeePropertyAssignment[]> {
  if (propertyIds.length === 0) return [];
  const rows = propertyIds.map((propertyId) => ({
    company_id: companyId,
    employee_id: employeeId,
    property_id: propertyId,
    assigned_by: assignedBy,
  }));
  const { data, error } = await supabase
    .from(TABLE_ASSIGNMENTS)
    .upsert(rows, { onConflict: "employee_id,property_id" })
    .select();
  if (error) throw error;
  return (data ?? []) as EmployeePropertyAssignment[];
}

async function unassignPropertiesViaSupabase(
  employeeId: string,
  companyId: string,
  propertyIds: string[],
): Promise<void> {
  const { error } = await supabase
    .from(TABLE_ASSIGNMENTS)
    .delete()
    .eq("employee_id", employeeId)
    .eq("company_id", companyId)
    .in("property_id", propertyIds);
  if (error) throw error;
}

async function clearAssignmentsViaSupabase(
  employeeId: string,
  companyId: string,
): Promise<void> {
  const { error } = await supabase
    .from(TABLE_ASSIGNMENTS)
    .delete()
    .eq("employee_id", employeeId)
    .eq("company_id", companyId);
  if (error) throw error;
}

// ── Employee ratings (Supabase implementations) ────────────────────────────

async function listRatingsViaSupabase(
  employeeId: string,
  companyId: string,
): Promise<EmployeeRating[]> {
  const { data, error } = await supabase
    .from(TABLE_RATINGS)
    .select("*")
    .eq("employee_id", employeeId)
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as EmployeeRating[];
}

async function listAllRatingsViaSupabase(
  companyId: string,
): Promise<EmployeeRating[]> {
  const { data, error } = await supabase
    .from(TABLE_RATINGS)
    .select("*")
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as EmployeeRating[];
}

async function createManagerRatingViaSupabase(
  companyId: string,
  employeeId: string,
  userId: string,
  rating: number,
  criteria: string,
  notes?: string,
): Promise<EmployeeRating> {
  const { data, error } = await supabase
    .from(TABLE_RATINGS)
    .insert({
      company_id: companyId,
      employee_id: employeeId,
      rating_type: "manager",
      rating,
      rated_by: userId,
      criteria,
      notes,
    })
    .select()
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Failed to create rating');
  return data as EmployeeRating;
}

// ── Repository ─────────────────────────────────────────────────────────────

export const employeesRepository = {
  // ── Employees CRUD ─────────────────────────────────────────────────────

  /**
   * List employees with derived verification status. Staff op — flipped to Nest.
   *
   * Track-E slice: reads from GET /employees/with-status (computes status
   * in NestJS/Prisma, byte-identical to the `employees_with_status` Supabase
   * view). Falls back to `listViaSupabase` which reads the view directly.
   *
   * super_admin: companyId is sent so the server scopes to the active company
   * (without it, scopeFor() returns null and lists ALL companies' employees).
   */
  async list(
    companyId: string,
    includeInactive: boolean = false,
  ): Promise<Employee[]> {
    const rows = await nestRequest<unknown[]>(
      API_ROUTES.employees.withStatus(includeInactive, companyId),
    );
    return (rows ?? []) as Employee[];
  },

  /**
   * Single employee by id with role-name enrichment. Staff op — flipped to Nest.
   */
  async getById(id: string, _companyId: string): Promise<Employee | null> {
    try {
      const row = await nestRequest<unknown>(API_ROUTES.employees.getById(id));
      return (row ?? null) as Employee | null;
    } catch (err) {
      if (isDefinitiveNotFound(err)) return null; // app-level 404 = the real answer (#3397 class)
      throw err;
    }
  },

  /**
   * Create a new employee. Staff op — flipped to Nest.
   *
   * NON-IDEMPOTENT write → duplicate-write guard (#3316 / #3399): the
   * Supabase fallback only runs when Nest verifiably did not commit (safe
   * 4xx / unconfigured). An ambiguous outcome (network, timeout, 5xx, parse
   * failure after a 2xx) RETHROWS instead of re-issuing the insert — a
   * fallback after a committed-but-lost Nest write would double-create the
   * employee.
   */
  async create(
    _companyId: string,
    data: CreateEmployeeData,
  ): Promise<Employee> {
    const row = await nestRequest<unknown>(API_ROUTES.employees.create(), {
      method: "POST",
      body: JSON.stringify(data),
    });
    return row as Employee;
  },

  /**
   * Update an employee. Staff op — flipped to Nest.
   * When role_permission_id changes, the Nest side also syncs user_role_assignments.
   */
  async update(
    id: string,
    _companyId: string,
    patch: Partial<Employee>,
  ): Promise<Employee | null> {
    const row = await nestRequest<unknown>(API_ROUTES.employees.update(id), {
      method: "PUT",
      body: JSON.stringify(patch),
    });
    return (row ?? null) as Employee | null;
  },

  /**
   * Soft-delete (deactivate) an employee. Staff op — flipped to Nest.
   */
  async remove(id: string, _companyId: string): Promise<void> {
    await nestRequest<void>(API_ROUTES.employees.remove(id), {
      method: "DELETE",
    });
  },

  /**
   * Count employees for a company. Staff op — flipped to Nest.
   *
   * `activeOnly` (default `true`) — pass `false` to count all employees
   * regardless of is_active (subscription usage counter pattern).
   */
  async count(companyId: string, activeOnly = true): Promise<number> {
    const result = await nestRequest<{ count: number }>(
      API_ROUTES.employees.count(companyId, activeOnly),
    );
    return result?.count ?? 0;
  },

  // ── Employee record by user_id (kept on Supabase — employee portal) ────

  /**
   * Get employee record by authenticated user id. Kept on Supabase — this is
   * called from the employee portal where the active role is "employee", which
   * lacks the `employees.view` permission key. A guarded Nest endpoint would 403.
   */
  async getRecordByUserId(userId: string): Promise<Employee | null> {
    return getRecordByUserIdViaSupabase(userId);
  },

  // ── Employee property assignments ──────────────────────────────────────

  /**
   * List property assignments for an employee. Staff op — flipped to Nest.
   */
  async listAssignments(
    employeeId: string,
    companyId: string,
  ): Promise<EmployeePropertyAssignment[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(
          API_ROUTES.employees.assignments.list(employeeId),
        );
        return (rows ?? []) as EmployeePropertyAssignment[];
      } catch (err) {
        reportNestFallback("employees.listAssignments", err);
      }
    }
    return listAssignmentsViaSupabase(employeeId, companyId);
  },

  /**
   * Assign properties to an employee (bulk upsert). Staff op — flipped to Nest.
   */
  async assignProperties(
    employeeId: string,
    companyId: string,
    propertyIds: string[],
    assignedBy: string | null,
  ): Promise<EmployeePropertyAssignment[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(
          API_ROUTES.employees.assignments.assign(employeeId),
          {
            method: "POST",
            body: JSON.stringify({ property_ids: propertyIds, assigned_by: assignedBy }),
          },
        );
        return (rows ?? []) as EmployeePropertyAssignment[];
      } catch (err) {
        reportNestFallback("employees.assignProperties", err);
      }
    }
    return assignPropertiesViaSupabase(employeeId, companyId, propertyIds, assignedBy);
  },

  /**
   * Unassign specific properties from an employee. Staff op — flipped to Nest.
   */
  async unassignProperties(
    employeeId: string,
    companyId: string,
    propertyIds: string[],
  ): Promise<void> {
    if (API_URL) {
      try {
        await nestRequest<void>(
          API_ROUTES.employees.assignments.unassign(employeeId),
          {
            method: "DELETE",
            body: JSON.stringify({ property_ids: propertyIds }),
          },
        );
        return;
      } catch (err) {
        reportNestFallback("employees.unassignProperties", err);
      }
    }
    return unassignPropertiesViaSupabase(employeeId, companyId, propertyIds);
  },

  /**
   * Clear all property assignments for an employee. Staff op — flipped to Nest.
   */
  async clearAssignments(
    employeeId: string,
    companyId: string,
  ): Promise<void> {
    if (API_URL) {
      try {
        await nestRequest<void>(
          API_ROUTES.employees.assignments.clear(employeeId),
          { method: "DELETE" },
        );
        return;
      } catch (err) {
        reportNestFallback("employees.clearAssignments", err);
      }
    }
    return clearAssignmentsViaSupabase(employeeId, companyId);
  },

  // ── Employee ratings ───────────────────────────────────────────────────

  /**
   * List ratings for a single employee. Staff op — flipped to Nest.
   */
  async listRatings(
    employeeId: string,
    companyId: string,
  ): Promise<EmployeeRating[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(
          API_ROUTES.employees.ratings.list(employeeId),
        );
        return (rows ?? []) as EmployeeRating[];
      } catch (err) {
        reportNestFallback("employees.listRatings", err);
      }
    }
    return listRatingsViaSupabase(employeeId, companyId);
  },

  /**
   * List all ratings in a company. Staff op — flipped to Nest.
   */
  async listAllRatings(companyId: string): Promise<EmployeeRating[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(
          API_ROUTES.employees.ratings.listAll(companyId),
        );
        return (rows ?? []) as EmployeeRating[];
      } catch (err) {
        reportNestFallback("employees.listAllRatings", err);
      }
    }
    return listAllRatingsViaSupabase(companyId);
  },

  /**
   * Create a manager rating for an employee. Staff op — flipped to Nest.
   *
   * NON-IDEMPOTENT write → duplicate-write guard (#3316 / #3399): the
   * Supabase fallback only runs when Nest verifiably did not commit (safe
   * 4xx / unconfigured). An ambiguous outcome (network, timeout, 5xx, parse
   * failure after a 2xx) RETHROWS instead of re-issuing the insert — a
   * fallback after a committed-but-lost Nest write would double-create the
   * rating row.
   */
  async createManagerRating(
    companyId: string,
    employeeId: string,
    userId: string,
    rating: number,
    criteria: string,
    notes?: string,
  ): Promise<EmployeeRating> {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(
          API_ROUTES.employees.ratings.create(employeeId),
          {
            method: "POST",
            body: JSON.stringify({
              rating,
              rated_by: userId,
              criteria,
              notes,
            }),
          },
        );
        return row as EmployeeRating;
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("employees.createManagerRating", err);
      }
    }
    return createManagerRatingViaSupabase(companyId, employeeId, userId, rating, criteria, notes);
  },

  /**
   * Create a customer rating request (`rating_requests` — the token row a
   * customer later redeems on the public CustomerRating page; distinct from
   * `employee_ratings`). Consolidated verbatim from useCreateRatingRequest
   * (fe-tail T10); Supabase-only — flip candidate alongside the
   * /employees/:id/ratings module.
   */
  async createRatingRequest(
    companyId: string,
    data: CreateRatingRequestData,
  ): Promise<RatingRequestRow> {
    const { data: row, error } = await supabase
      .from("rating_requests")
      .insert({
        company_id: companyId,
        employee_id: data.employeeId,
        task_id: data.taskId,
        customer_name: data.customerName,
        customer_phone: data.customerPhone || null,
        customer_email: data.customerEmail || null,
      })
      .select()
      .maybeSingle();

    if (error) throw error;
    // No row means the insert's RLS read-back was filtered, not that the
    // write failed — surface that plainly instead of PGRST116.
    if (!row) throw new Error("rating_requests insert returned no row");
    return row as RatingRequestRow;
  },
};
